import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  GeckoModal,
  GECKO_CONSTS,
  GeckoFields,
  GeckoModalService,
  GeckoToastService,
} from 'ui';
import GeckoFeatures from 'services/gecko-features';
import FilterBuilder from 'components/BuildableFilter/FilterBuilder';
import AgentSelect from 'components/AgentSelect';
import TeamSelect from 'components/TeamSelect';
import BotSelect from 'components/BotSelect';
import ChannelSelect from 'components/ChannelSelect';
import TagSelect from 'components/TagSelect';
import CONSTANTS from 'chat-constants';
import {
  createSavedCustomFilter,
  updateSavedCustomFilter,
} from '../../actions';
import { getSavedCustomFiltersPinLimitReached } from 'selectors';
import './index.scss';

function FilterModal({
  existingConfig,
  currentFilters = [],
  onFiltersChanged,
}) {
  const [filters, setFilters] = useState(currentFilters);
  const [saveStepActive, setSaveStepActive] = useState(false);
  const [filterName, _setFilterName] = useState(existingConfig?.name || '');
  const [pinFilter, setPinFilter] = useState(existingConfig?.pinned || false);
  const pinLimitReached = useSelector(getSavedCustomFiltersPinLimitReached);
  const isEditing = Boolean(currentFilters.length);
  const isUpdating = Boolean(existingConfig?.id);
  const pinningIsDisabled = !existingConfig?.pinned && pinLimitReached;

  const statusField = {
    key: 'status',
    type: 'choice',
    label: 'Status',
    choices: [
      {
        label: 'Open',
        value: 'open',
      },
      {
        label: 'Closed',
        value: 'closed',
      },
      // https://geckolabs.atlassian.net/browse/CHT-540?focusedCommentId=32885
      // Leaving out for now until we decide if we want this as an option
      // {
      //     label: 'Sent',
      //     value: 'sent'
      // }
    ],
  };
  const agentField = {
    key: 'agent',
    type: 'custom',
    label: 'Agent',
    operators: [
      { label: 'is', value: 'custom:agent is' },
      { label: 'is not', value: 'custom:agent is not' },
    ],
    field: <AgentSelect />,
  };
  const teamField = {
    key: 'team',
    type: 'custom',
    label: 'Team',
    operators: [
      { label: 'is', value: 'custom:team is' },
      { label: 'is not', value: 'custom:team is not' },
    ],
    field: <TeamSelect restrictToUserTeams={false} />,
  };
  const botField = {
    key: 'bot',
    type: 'custom',
    label: 'Last Bot',
    operators: [
      { label: 'is', value: 'custom:bot is' },
      { label: 'is not', value: 'custom:bot is not' },
    ],
    field: <BotSelect multi={true} />,
  };
  const channelField = {
    key: 'lastChannel',
    type: 'custom',
    label: 'Last Channel',
    operators: [
      { label: 'is', value: 'custom:last channel is' },
      { label: 'is not', value: 'custom:last channel is not' },
    ],
    field: (
      <ChannelSelect
        multi={true}
        allowedChannelTypes={[
          ...CONSTANTS.CHANNEL.PICKABLE_TYPES,
          CONSTANTS.CHANNEL.TYPE_FORM_RESPONSE,
        ]}
      />
    ),
  };
  const conversationStartedField = {
    key: 'startedAt',
    type: 'custom',
    label: 'Started',
    operators: [
      { label: 'is', value: 'date:is' },
      { label: 'is after', value: 'date:is after' },
      { label: 'is before', value: 'date:is before' },
      { label: 'is between', value: 'daterange:is between' },
    ],
  };
  const tagField = {
    key: 'tag',
    type: 'custom',
    label: 'Tag',
    operators: [
      { label: 'is', value: 'custom:tag is' },
      { label: 'is not', value: 'custom:tag is not' },
    ],
    field: <TagSelect multi={true} />,
  };

  const sourceField = {
    key: 'source',
    type: 'choice',
    label: 'Source',
    choices: [
      {
        label: 'Chat GPT',
        value: 'chatgpt',
      },
      {
        label: 'IBM Watson',
        value: 'watson',
      },
    ],
  };

  const fields = [
    statusField,
    agentField,
    teamField,
    GeckoFeatures.can('chat-bot') ? botField : null,
    channelField,
    conversationStartedField,
    tagField,
    sourceField,
  ].filter((field) => field !== null);

  const saveStepFields = [
    { key: 'filterName', label: 'Filter Name', width: '100%' },
    {
      key: 'pinFilter',
      type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
      label: 'Pin saved filter to toolbar:',
      disabled: pinningIsDisabled,
      width: '100%',
      description: pinningIsDisabled
        ? 'You already have 2 pinned custom filters. Please remove one to be able to pin more.'
        : '',
    },
  ];

  const setFilterName = (value) => {
    if (value.length > 15) return;
    _setFilterName(value);
  };

  const saveFieldHandler = (key, value) => {
    const fields = {
      filterName: setFilterName,
      pinFilter: setPinFilter,
    };

    fields[key](value);
  };

  const modalButtons = [
    {
      name: 'Clear Filter',
      type: 'default',
      handler: () => setFilters([]),
    },
    {
      name: 'Save Filter',
      type: 'default',
      handler: () => setSaveStepActive(true),
      disabledWhen: () => !filters.length,
    },
    {
      name: 'Apply Filter',
      type: 'primary',
      handler: () => onFiltersChanged(filters),
    },
  ];

  const handleSaveCustomFilter = async () => {
    try {
      const details = { name: filterName, config: filters };
      if (isUpdating) {
        const { id, pinned, removeCustomFilter } = existingConfig;
        await updateSavedCustomFilter(id, details, pinFilter);
        if (pinned && !pinFilter) {
          removeCustomFilter(id);
        }
      } else {
        await createSavedCustomFilter(details, pinFilter);
      }
      new GeckoModalService().close();
      new GeckoToastService().success(
        `Custom filter ${isUpdating ? 'updated' : 'created'} successfully`
      );
    } catch (error) {
      new GeckoToastService().error();
    }
  };

  const saveStepModalButtons = [
    {
      name: 'Back',
      type: 'default',
      handler: () => setSaveStepActive(false),
    },
    {
      name: 'Save Filter',
      type: 'primary',
      handler: handleSaveCustomFilter,
      disabledWhen: () => !filterName,
    },
  ];
  const modalName = (
    <span>{isEditing ? 'Edit' : 'Create'} Conversation Filter</span>
  );
  const btns = saveStepActive ? saveStepModalButtons : modalButtons;
  return (
    <div className="FilterModal">
      <GeckoModal name={modalName} btns={btns}>
        {saveStepActive ? (
          <GeckoFields
            fields={saveStepFields}
            values={{ filterName, pinFilter }}
            handler={saveFieldHandler}
          />
        ) : (
          <FilterBuilder
            fields={fields}
            filters={filters}
            onChange={setFilters}
          />
        )}
      </GeckoModal>
    </div>
  );
}

export default FilterModal;
